import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdDescription,
  MdDownload,
  MdSearch,
  MdSettings,
} from 'react-icons/md';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import marksAPI from '../api/marksAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import '../../employee-attendance/employeeAttendance.css';
import '../../employee-attendance/employeeAttendance-hr.css';
import '../../employee-attendance/employeeAttendance-admissions.css';

const ROMAN = [
  'I', 'II', 'III', 'IV', 'V', 'VI',
  'VII', 'VIII', 'IX', 'X', 'XI', 'XII',
];

const SEMESTERS = ROMAN.map((roman, idx) => ({
  value: String(idx + 1),
  label: `Semester ${roman}`,
}));

/** Mint / green palette from the printed Official Transcript */
const TR = {
  pageBg: [233, 244, 236],
  box: [169, 220, 201],
  border: [47, 79, 71],
  text: [16, 38, 31],
  white: [255, 255, 255],
};

function semesterLabel(value) {
  return SEMESTERS.find((s) => s.value === String(value))?.label || '';
}

function Field({ label, help, children }) {
  return (
    <div className="ea-odoo-field">
      <label className="ea-odoo-field__label">
        {label}
        {help ? (
          <sup className="ea-marks-search__help" title={help}>
            ?
          </sup>
        ) : null}
      </label>
      <div className="ea-odoo-field__value">{children}</div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="ea-odoo-group">
      <h3 className="ea-odoo-group__title">{title}</h3>
      <div className="ea-odoo-group__body">{children}</div>
    </div>
  );
}

function fmtMark(value) {
  if (value === null || value === undefined || value === '') return 'N/A';
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(1) : 'N/A';
}

function fmtPercent(value) {
  return `${(Number(value) || 0).toFixed(2)}%`;
}

function drawFilledRect(doc, x, y, w, h, fill, border) {
  doc.setFillColor(...fill);
  doc.setDrawColor(...border);
  doc.setLineWidth(0.35);
  doc.rect(x, y, w, h, 'FD');
}

function pairCourseCounts(cards) {
  const pairCount = Math.max(1, Math.ceil(cards.length / 2));
  return Array.from({ length: pairCount }, (_, row) => {
    const left = cards[row * 2]?.courses?.length || 0;
    const right = cards[row * 2 + 1]?.courses?.length || 0;
    return Math.max(left, right);
  });
}

function scaleFits(measured, maxEndY) {
  return measured.pages === 1 && measured.lastY <= maxEndY + 0.35;
}

function measureSemesterTables(cards, size, layout) {
  const probe = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  return drawSemesterTables(probe, cards, size, layout);
}

function drawSemesterTables(doc, cards, size, layout) {
  const { tablesStart, colW, leftColX, rightColX, pageW, maxEndY } = layout;
  const rowHeights = [];
  const rowStarts = [];
  const bottomMargin = Math.max(8, doc.internal.pageSize.getHeight() - (maxEndY || 280));
  const tableStyles = {
    theme: 'grid',
    styles: {
      fontSize: size.font,
      cellPadding: { top: size.pad, bottom: size.pad, left: 1.4, right: 1.4 },
      textColor: TR.text,
      lineColor: TR.border,
      lineWidth: 0.2,
      overflow: 'linebreak',
      minCellHeight: size.minH,
      valign: 'middle',
    },
    headStyles: {
      fillColor: TR.box,
      textColor: TR.text,
      fontStyle: 'bold',
      fontSize: size.head,
      halign: 'center',
      valign: 'middle',
      minCellHeight: size.minH + 0.4,
    },
    bodyStyles: {
      fillColor: TR.white,
    },
  };

  cards.forEach((card, index) => {
    const column = index % 2;
    const row = Math.floor(index / 2);
    const tableX = column === 0 ? leftColX : rightColX;
    let cursorY;

    if (column === 0) {
      cursorY = row === 0 ? tablesStart : rowStarts[row - 1] + rowHeights[row - 1] + size.gap;
      rowStarts[row] = cursorY;
    } else {
      cursorY = rowStarts[row];
    }

    const body = (card.courses || []).map((c) => [c.name || '', fmtMark(c.marks)]);
    body.push([
      {
        content: 'Semester GPA',
        styles: { fontStyle: 'bold', fillColor: TR.box, valign: 'middle' },
      },
      {
        content: fmtPercent(card.average),
        styles: { halign: 'right', fontStyle: 'bold', fillColor: TR.box, valign: 'middle' },
      },
    ]);

    autoTable(doc, {
      ...tableStyles,
      startY: cursorY,
      margin: {
        left: tableX,
        right: pageW - tableX - colW,
        bottom: bottomMargin,
      },
      tableWidth: colW,
      showHead: 'firstPage',
      pageBreak: 'avoid',
      rowPageBreak: 'avoid',
      head: [
        [
          {
            content: card.label || `Semester ${card.semester}`,
            colSpan: 2,
            styles: {
              halign: 'center',
              fillColor: TR.box,
              fontStyle: 'bold',
              fontSize: size.title,
              minCellHeight: size.minH + 0.8,
            },
          },
        ],
        [
          { content: 'Course', styles: { halign: 'left' } },
          { content: 'Marks', styles: { halign: 'right' } },
        ],
      ],
      body,
      columnStyles: {
        0: { cellWidth: colW * 0.84, halign: 'left' },
        1: { cellWidth: colW * 0.16, halign: 'right' },
      },
    });

    const usedH = doc.lastAutoTable.finalY - rowStarts[row];
    if (!rowHeights[row] || rowHeights[row] < usedH) {
      rowHeights[row] = usedH;
    }
  });

  const lastRow = Math.max(0, Math.ceil(cards.length / 2) - 1);
  const lastY =
    cards.length === 0
      ? tablesStart
      : rowStarts[lastRow] + rowHeights[lastRow];

  return {
    rowHeights,
    rowStarts,
    lastY,
    pages: doc.getNumberOfPages(),
  };
}

function shrinkSize(size, factor = 0.92) {
  return {
    font: Math.max(6.4, +(size.font * factor).toFixed(2)),
    head: Math.max(6.8, +(size.head * factor).toFixed(2)),
    title: Math.max(7.4, +(size.title * factor).toFixed(2)),
    pad: Math.max(0.35, +(size.pad * factor).toFixed(2)),
    gap: Math.max(1.5, +(size.gap * 0.94).toFixed(2)),
    minH: Math.max(2.8, +(size.minH * factor).toFixed(2)),
  };
}

function pickReadableTranscriptSize(cards, maxEndY, layout) {
  const pairCount = Math.max(1, Math.ceil(cards.length / 2));
  const dense = pairCount >= 4;
  const candidates = dense
    ? [
        { font: 8.4, head: 8.8, title: 9.6, pad: 0.85, gap: 2.4, minH: 4.4 },
        { font: 7.8, head: 8.2, title: 9.0, pad: 0.7, gap: 2.1, minH: 4.0 },
        { font: 7.3, head: 7.7, title: 8.5, pad: 0.58, gap: 1.8, minH: 3.6 },
        { font: 6.8, head: 7.2, title: 8.0, pad: 0.48, gap: 1.6, minH: 3.3 },
      ]
    : [
        { font: 11, head: 11.5, title: 13, pad: 2.0, gap: 4.6, minH: 6.8 },
        { font: 10.2, head: 10.8, title: 12, pad: 1.7, gap: 4.0, minH: 6.0 },
        { font: 9.4, head: 10, title: 11, pad: 1.4, gap: 3.5, minH: 5.3 },
        { font: 8.6, head: 9.2, title: 10.2, pad: 1.15, gap: 3.0, minH: 4.7 },
        { font: 7.8, head: 8.4, title: 9.4, pad: 0.9, gap: 2.4, minH: 4.1 },
      ];

  let chosen = candidates[candidates.length - 1];
  let measured = measureSemesterTables(cards, chosen, layout);

  for (const size of candidates) {
    const result = measureSemesterTables(cards, size, layout);
    if (scaleFits(result, maxEndY)) {
      chosen = size;
      measured = result;
      break;
    }
  }

  while (!scaleFits(measured, maxEndY)) {
    const next = shrinkSize(chosen);
    if (
      next.font === chosen.font &&
      next.minH === chosen.minH &&
      next.pad === chosen.pad
    ) {
      break;
    }
    chosen = next;
    measured = measureSemesterTables(cards, chosen, layout);
  }

  const leftover = scaleFits(measured, maxEndY) ? maxEndY - measured.lastY : 0;
  const counts = pairCourseCounts(cards);
  const stretchRows = counts.reduce((sum, n) => sum + n + 3, 0) || 1;
  let extra = leftover > 3 ? Math.min(3.6, leftover / stretchRows) : 0;
  let stretched = { ...chosen, minH: chosen.minH + extra };
  let check = measureSemesterTables(cards, stretched, layout);
  while (extra > 0.12 && !scaleFits(check, maxEndY)) {
    extra *= 0.62;
    stretched = { ...chosen, minH: chosen.minH + extra };
    check = measureSemesterTables(cards, stretched, layout);
  }

  return scaleFits(check, maxEndY) ? stretched : chosen;
}

function buildOfficialTranscriptPdf(transcript, student) {
  const cards = transcript.semesters || [];
  const pairCount = Math.max(1, Math.ceil(cards.length / 2));
  const dense = pairCount >= 4;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = dense ? 8 : 10;
  const contentW = pageW - margin * 2;
  const gap = dense ? 3.2 : 5;

  doc.setFillColor(...TR.pageBg);
  doc.rect(0, 0, pageW, pageH, 'F');
  doc.setTextColor(...TR.text);

  const infoY = margin;
  const infoH = dense ? 24 : 28;
  const infoFont = dense ? 9.5 : 11;
  drawFilledRect(doc, margin, infoY, contentW, infoH, TR.box, TR.border);
  doc.setDrawColor(...TR.border);
  doc.setLineWidth(0.35);
  doc.line(margin + contentW / 2, infoY, margin + contentW / 2, infoY + infoH);

  const leftX = margin + 4;
  const rightX = margin + contentW / 2 + 4;
  const lineH = dense ? 5.6 : 6.4;
  const y0 = infoY + (dense ? 5.6 : 6.4);

  const infoLine = (x, y, label, value) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(infoFont);
    doc.setTextColor(...TR.text);
    doc.text(label, x, y);
    const lw = doc.getTextWidth(label) + 2;
    doc.setFont('helvetica', 'normal');
    doc.text(String(value || ''), x + lw, y, { maxWidth: contentW / 2 - lw - 7 });
  };

  infoLine(leftX, y0, 'Student ID:', student?.student_no);
  infoLine(leftX, y0 + lineH, 'Student Name:', student?.name);
  infoLine(leftX, y0 + lineH * 2, 'Batch:', student?.batch_name);

  infoLine(rightX, y0, 'Faculty:', student?.faculty_name);
  infoLine(rightX, y0 + lineH, 'Degree:', student?.degree);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(infoFont);
  doc.text('Graduation Date:', rightX, y0 + lineH * 2);
  const gx = rightX + doc.getTextWidth('Graduation Date:') + 2;
  doc.setFont('helvetica', 'normal');
  doc.text(String(student?.graduation_date || ''), gx, y0 + lineH * 2);

  doc.setFont('helvetica', 'bold');
  const issueLabel = 'Issue Date:';
  const issueVal = String(transcript.issue_date || '');
  const issueW = doc.getTextWidth(issueLabel) + 2 + doc.getTextWidth(issueVal);
  const issueX = margin + contentW - issueW - 4;
  doc.text(issueLabel, issueX, y0 + lineH * 2);
  doc.setFont('helvetica', 'normal');
  doc.text(issueVal, issueX + doc.getTextWidth(issueLabel) + 2, y0 + lineH * 2);

  const colW = (contentW - gap) / 2;
  const noteH = dense ? 8.5 : 10;
  const sigBlock = dense ? 18 : 22;
  const footerGap = 4;
  const noteY = pageH - margin - sigBlock - noteH;
  const maxEndY = noteY - footerGap;
  const tablesStart = infoY + infoH + (dense ? 3.5 : 5);
  const layout = {
    tablesStart,
    colW,
    leftColX: margin,
    rightColX: margin + colW + gap,
    pageW,
    gap,
    maxEndY,
  };
  const size = pickReadableTranscriptSize(cards, maxEndY, layout);
  drawSemesterTables(doc, cards, size, layout);

  const halfW = (contentW - gap) / 2;
  const footFont = dense ? 9.5 : 11;
  drawFilledRect(doc, margin, noteY, halfW, noteH, TR.box, TR.border);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(footFont);
  doc.setTextColor(...TR.text);
  doc.text('The Medium of Instruction is English Language', margin + halfW / 2, noteY + noteH * 0.65, {
    align: 'center',
    maxWidth: halfW - 5,
  });

  drawFilledRect(doc, margin + halfW + gap, noteY, halfW, noteH, TR.box, TR.border);
  const summary = transcript.summary || {};
  doc.text('Graduation CGPA:', margin + halfW + gap + 4, noteY + noteH * 0.65);
  doc.text(fmtPercent(summary.average), margin + halfW + gap + halfW - 4, noteY + noteH * 0.65, {
    align: 'right',
  });

  const sigY = noteY + noteH + 7;
  const roles = ['General Registrar', 'Dean of the Faculty'];
  const sigW = contentW / roles.length;
  roles.forEach((role, i) => {
    const x = margin + sigW * i + sigW / 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(footFont);
    doc.text(role, x, sigY, { align: 'center' });
    doc.setDrawColor(...TR.border);
    doc.setLineWidth(0.4);
    doc.line(x - 34, sigY + 11, x + 34, sigY + 11);
  });

  return doc;
}

function TranscriptRow({ label, value }) {
  return (
    <p className="ea-tr-row">
      <span className="ea-tr-row__label">{label}</span>
      <span className="ea-tr-row__value">{value || ''}</span>
    </p>
  );
}

function SemesterCard({ card }) {
  return (
    <table className="ea-tr-card">
      <thead>
        <tr className="ea-tr-card__title">
          <th colSpan={2}>{card.label}</th>
        </tr>
        <tr className="ea-tr-card__head">
          <th className="ea-tr-c-course">Course</th>
          <th className="ea-tr-c-mark">Marks</th>
        </tr>
      </thead>
      <tbody>
        {card.courses.map((course) => (
          <tr key={`${card.semester}-${course.subject_id}-${course.name}`}>
            <td className="ea-tr-c-course">{course.name}</td>
            <td className="ea-tr-c-mark">{fmtMark(course.marks)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="ea-tr-card__foot">
          <td>Semester GPA</td>
          <td className="ea-tr-c-mark">{fmtPercent(card.average)}</td>
        </tr>
      </tfoot>
    </table>
  );
}

export default function SearchStudentMarks() {
  const { getToken, user } = useAttendanceAuth();
  const [studentNo, setStudentNo] = useState('');
  const [semester, setSemester] = useState('');
  const [student, setStudent] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const studentIdRef = useRef(null);

  const transcriptReady = Boolean(transcript?.semesters?.length);

  // Match the Exam Portal workflow: resolve the student while the ID is typed,
  // then load the official transcript automatically when a semester is chosen.
  useEffect(() => {
    const trimmed = studentNo.trim();
    setTranscript(null);
    setError(null);
    if (!trimmed) {
      setStudent(null);
      setWarning(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const token = getToken();
        if (!token) throw new Error('Authentication token not found. Please login again.');

        const data = semester
          ? await marksAPI.getStudentTranscript(trimmed, semester, token)
          : await marksAPI.lookupStudent(trimmed, token);
        if (cancelled) return;

        const resolvedStudent = data?.student || null;
        if (!resolvedStudent || (!semester && !data?.found)) {
          setStudent(null);
          setWarning(data?.message || `No student found with ID "${trimmed}".`);
          return;
        }

        const userFacultyId = user?.faculty?.id || user?.faculty_id;
        const studentFacultyId = resolvedStudent.faculty_id;
        if (
          userFacultyId &&
          studentFacultyId &&
          Number(userFacultyId) !== Number(studentFacultyId)
        ) {
          setStudent(null);
          setWarning(
            `This student belongs to "${resolvedStudent.faculty_name || 'a different faculty'}". ` +
              `You can only search students in ${user?.faculty?.name || 'your assigned faculty'}.`
          );
          return;
        }

        setStudent(resolvedStudent);
        setWarning(null);
        if (semester) {
          if (!data?.graded_count) {
            setError(
              `No marks found for student ${resolvedStudent.name || ''} (ID: ${
                resolvedStudent.student_no || trimmed
              }) up to ${semesterLabel(semester)}`
            );
            return;
          }
          setTranscript(data);
        }
      } catch (err) {
        if (cancelled) return;
        setStudent(null);
        setWarning(null);
        setError(err.message || 'Failed to search student marks. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [studentNo, semester, getToken, user]);
  const semesterPairs = useMemo(() => {
    const cards = transcript?.semesters || [];
    const pairs = [];
    for (let i = 0; i < cards.length; i += 2) {
      pairs.push([cards[i], cards[i + 1] || null]);
    }
    return pairs;
  }, [transcript]);

  const onNewSearch = () => {
    setStudentNo('');
    setSemester('');
    setStudent(null);
    setTranscript(null);
    setWarning(null);
    setError(null);
    studentIdRef.current?.focus();
  };

  const onDownloadPdf = () => {
    if (!transcriptReady) return;
    setDownloading(true);
    setError(null);
    try {
      const doc = buildOfficialTranscriptPdf(transcript, student);
      doc.save(`Official_Transcript_${student?.student_no || 'student'}.pdf`);
    } catch (err) {
      setError(err.message || 'Could not build the transcript PDF.');
    } finally {
      setDownloading(false);
    }
  };

  const summary = transcript?.summary;

  return (
    <div className="ea-odoo-form-page ea-adm-form ea-exam-form ea-marks-search">
      <div className="ea-odoo-form-bar ea-adm-cp">
        <div className="ea-adm-cp__left">
          <button type="button" className="ea-odoo-new ea-adm-cp__new" onClick={onNewSearch}>
            New
          </button>
          <div className="ea-adm-cp__title-block">
            <span className="ea-adm-cp__parent">Search Student Marks</span>
            <div className="ea-adm-cp__record-row">
              <span className="ea-adm-cp__record">
                {transcriptReady ? student?.student_no || 'Student marks' : 'New'}
              </span>
              <button type="button" className="ea-adm-cp__icon" title="Actions" aria-label="Actions">
                <MdSettings size={16} />
              </button>
              {studentNo || semester ? (
                <button
                  type="button"
                  className="ea-adm-cp__icon"
                  onClick={onNewSearch}
                  title="Discard"
                  aria-label="Discard"
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>
        </div>
        {transcriptReady ? (
          <div className="ea-adm-cp__right">
            <div className="ea-adm-cp__pager">
              <span>1 / 1</span>
              <span className="ea-adm-cp__pager-btns">
                <button type="button" className="ea-odoo-pager__btn" disabled aria-label="Previous">
                  <MdChevronLeft size={16} />
                </button>
                <button type="button" className="ea-odoo-pager__btn" disabled aria-label="Next">
                  <MdChevronRight size={16} />
                </button>
              </span>
            </div>
          </div>
        ) : null}
      </div>

      {error && (
        <div className="ea-odoo-alert ea-odoo-alert--error" role="alert">
          {error}
        </div>
      )}
      {warning && !error && (
        <div className="ea-odoo-alert ea-odoo-alert--warning" role="status">
          <strong>Student not found:</strong> {warning}
        </div>
      )}

      <form className="ea-odoo-sheet ea-exam-sheet" onSubmit={(e) => e.preventDefault()}>
        <div className="ea-odoo-sheet__body ea-exam-sheet__body">
          <div className="ea-odoo-cols">
            <Group title="Search Criteria">
              <Field label="Student ID" help="Enter the student ID number">
                <input
                  ref={studentIdRef}
                  value={studentNo}
                  onChange={(e) => setStudentNo(e.target.value)}
                  placeholder="Enter Student ID"
                  autoComplete="off"
                />
              </Field>
              <Field label="Semester">
                <select
                  className={semester ? undefined : 'is-empty'}
                  value={semester}
                  onChange={(e) => setSemester(e.target.value)}
                >
                  <option value="">Select Semester</option>
                  {SEMESTERS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </Field>
            </Group>

            <Group title="Student Info">
              <Field label="Student Name">
                <span className="ea-marks-search__ro">{student?.name || ''}</span>
              </Field>
              <Field label="Faculty">
                <span className="ea-marks-search__ro">{student?.faculty_name || ''}</span>
              </Field>
              <Field label="Department">
                <span className="ea-marks-search__ro">{student?.department_name || ''}</span>
              </Field>
              <Field label="Batch">
                <span className="ea-marks-search__ro">{student?.batch_name || ''}</span>
              </Field>
            </Group>
          </div>

          {transcriptReady ? (
            <div className="ea-marks-search__actions">
              <span />
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--ghost ea-marks-search__btn"
                onClick={onDownloadPdf}
                disabled={downloading}
              >
                <MdDownload size={16} aria-hidden />
                {downloading ? 'Preparing PDF…' : 'Download PDF'}
              </button>
            </div>
          ) : (
            <div className="ea-marks-search__empty">
              {loading ? (
                <>
                  <MdSearch size={46} aria-hidden />
                  <p>Loading transcript…</p>
                </>
              ) : (
                <>
                  <MdDescription size={46} aria-hidden />
                  <p>
                    Enter a student ID and pick a semester — the transcript loads automatically.
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {transcriptReady ? (
          <>
            <div className="ea-odoo-tabs ea-exam-tabs">
              <span className="ea-odoo-tabs__btn active">Transcript Preview</span>
            </div>
            <div className="ea-exam-notebook__body">
              <div className="ea-transcript-preview">
                <div
                  className={`ea-tr-page${
                    semesterPairs.length >= 4 ? ' ea-tr-page--dense' : ''
                  }`}
                >
                  <div className="ea-tr-info">
                    <div className="ea-tr-info__col">
                      <TranscriptRow label="Student ID:" value={student?.student_no} />
                      <TranscriptRow label="Student Name:" value={student?.name} />
                      <TranscriptRow label="Batch:" value={student?.batch_name} />
                    </div>
                    <div className="ea-tr-info__col">
                      <TranscriptRow label="Faculty:" value={student?.faculty_name} />
                      <TranscriptRow label="Degree:" value={student?.degree} />
                      <p className="ea-tr-row ea-tr-row--split">
                        <span>
                          <span className="ea-tr-row__label">Graduation Date:</span>
                          <span className="ea-tr-row__value">{student?.graduation_date || ''}</span>
                        </span>
                        <span>
                          <span className="ea-tr-row__label">Issue Date:</span>
                          <span className="ea-tr-row__value">{transcript.issue_date || ''}</span>
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="ea-tr-grid">
                    {semesterPairs.map(([left, right]) => (
                      <div className="ea-tr-grid__row" key={`pair-${left.semester}`}>
                        <SemesterCard card={left} />
                        {right ? <SemesterCard card={right} /> : <div />}
                      </div>
                    ))}
                  </div>

                  <div className="ea-tr-bottom">
                    <div className="ea-tr-note">
                      The Medium of Instruction is English Language
                    </div>
                    <div className="ea-tr-cgpa">
                      <span>Graduation CGPA:</span>
                      <b>{fmtPercent(summary?.average)}</b>
                    </div>
                  </div>

                  <div className="ea-tr-signatures">
                    <div>
                      <span className="ea-tr-signatures__role">General Registrar</span>
                      <span className="ea-tr-signatures__line" />
                    </div>
                    <div>
                      <span className="ea-tr-signatures__role">Dean of the Faculty</span>
                      <span className="ea-tr-signatures__line" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </form>
    </div>
  );
}
